import React, { useState } from 'react';
import { ConfigurationControls } from 'src/pages/ConfigurationPage/ConfigurationControls/ConfigurationControls';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { ConfigurationSettingsVersions } from 'src/pages/ConfigurationPage/ConfigurationSettingsVersions/ConfigurationSettingsVersions';
import { PlaygroundChat } from 'src/components/PlaygroundChat/PlaygroundChat';
import CardContainer from 'src/components/CardContainer/CardContainer';
import { clsx } from 'clsx';
import { Tabs, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { Separator } from 'src/components/ui/separator';
import { PersonaTab } from 'src/pages/ConfigurationPage/PersonaTab/PersonaTab';
import { DataSourcesTab } from 'src/pages/ConfigurationPage/DataSourcesTab/DataSourcesTab';
import { WorkflowsTab } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTab';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { SystemPromptTab } from 'src/pages/ConfigurationPage/SystemPromptTab/SystemPromptTab';
import { FormProvider } from 'react-hook-form';
import { UnsavedChangesPopup } from 'src/pages/ConfigurationPage/UnsavedChangesPopup/UnsavedCghangesPopup';
import { NotDeployedSettingsPopup } from 'src/pages/ConfigurationPage/NotDeployedSettingsPopup/NotDeployedSettingsPopup';
import { ProjectTitle } from 'src/pages/ControlDeskPage/ProjectTitle/ProjectTitle';
import { useConfigurationContext } from 'src/provider/ConfigurationProvider';
import { useConfigurationTemplateData } from 'src/pages/ConfigurationPage/hooks/useConfigurationTemplateData';
import { useConfigurationSettingsList } from 'src/pages/ConfigurationPage/hooks/useConfigurationSettingsList';
import { useConfigurationPrompt } from 'src/pages/ConfigurationPage/hooks/useConfigurationPrompt';
import { useConfigurationFormFields } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import { useConfigurationForm } from 'src/pages/ConfigurationPage/hooks/useConfigurationForm';
import { useConfigurationWorkflows } from 'src/pages/ConfigurationPage/hooks/useConfigurationWorkflows';
import { useResetFormOnReadonlyChange } from 'src/pages/ConfigurationPage/hooks/useResetFormOnReadonlyChange';
import { useConfigurationSettingsMutations } from 'src/pages/ConfigurationPage/hooks/useConfigurationSettingsMutations';
import { useConfigurationSubmit } from 'src/pages/ConfigurationPage/hooks/useConfigurationSubmit';
import { useConfigurationLoadingState } from 'src/pages/ConfigurationPage/hooks/useConfigurationLoadingState';
import { useConfigurationActiveTab } from 'src/pages/ConfigurationPage/hooks/useConfigurationActiveTab';
import { useFullScreenToggle } from 'src/pages/ConfigurationPage/hooks/useFullScreenToggle';
import { shouldEnterEditModeOnClick } from 'src/pages/ConfigurationPage/utils/shouldEnterEditModeOnClick';

const ConfigurationPage = () => {
  const { id } = useParams();
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);
  const { setIsEditWorkflowMode, setSelectedWorkflow } = useConfigurationContext();

  const [readonly, setReadonly] = useState(true);
  const { isFullScreen, toggle: fullScreenBtnClickHandler } = useFullScreenToggle();

  const {
    defaultTemplateFields,
    isLoadingDefaultTemplateFields,
    customTemplatesData,
    isLoadingCustomTemplates,
  } = useConfigurationTemplateData();

  const {
    settings,
    activeSettings,
    setActiveSettings,
    isCreationMode,
    setIsCreationMode,
    isLoadingAllConfigurationSettings,
    resetToFirstPage,
    restoreActiveSettingsFallback,
  } = useConfigurationSettingsList(id, defaultTemplateFields);

  const {
    configurationSettingsPrompt,
    isLoadingConfigurationSettingsPrompt,
    isFetchingConfigurationSettingsPrompt,
  } = useConfigurationPrompt(id, activeSettings?.id, Boolean(defaultTemplateFields));

  const {
    formFields,
    resetToTemplateDefaults,
    inputData,
    systemPromptActive,
    setSystemPromptActive,
    formFieldsObject,
  } = useConfigurationFormFields({
    defaultTemplateFields,
    configurationSettingsPrompt,
    hasNoSettingsYet: !activeSettings && settings.length === 0,
  });

  const methods = useConfigurationForm({ inputData, formFieldsObject, systemPromptActive });

  const {
    defaultWorkflows,
    customWorkflows,
    setDefaultWorkflows,
    setCustomWorkflows,
    onWorkflowsChange,
    onWorkflowAdd,
  } = useConfigurationWorkflows({ formFieldsObject, inputData, methods });

  useResetFormOnReadonlyChange({
    readonly,
    methods,
    inputData,
    formFieldsObject,
    setDefaultWorkflows,
    setCustomWorkflows,
    setIsEditWorkflowMode,
    setSelectedWorkflow,
  });

  const {
    createConfigurationSettings,
    updateConfigurationSettings,
    deployConfigurationSettings,
    isSubmitting,
    isLoadingCreateConfigurationSettings,
    isLoadingUpdateConfigurationSettings,
    isLoadingDeployConfigurationSettings,
  } = useConfigurationSettingsMutations({
    onCreated: (data) => {
      setActiveSettings(data);
      setIsCreationMode(false);
      setReadonly(true);
      resetToFirstPage();
    },
    onUpdated: () => setReadonly(true),
    onDeployed: () => resetToFirstPage(),
  });

  const { submitForm } = useConfigurationSubmit({
    systemPromptActive,
    formFieldsObject,
    inputData,
    isCreationMode,
    activeSettings,
    configurationSettingsPrompt,
    defaultTemplateFields,
    defaultWorkflows,
    customWorkflows,
    id,
    methods,
    createConfigurationSettings,
    updateConfigurationSettings,
    isSubmitting,
  });

  const [activeTabId, setActiveTabId] = useConfigurationActiveTab(systemPromptActive);

  const isLoading = useConfigurationLoadingState([
    isLoadingCreateConfigurationSettings,
    isLoadingDefaultTemplateFields,
    isLoadingDeployConfigurationSettings,
    isLoadingAllConfigurationSettings,
    isLoadingConfigurationSettingsPrompt,
    isFetchingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    isLoadingCustomTemplates,
  ]);

  const onSettingsChange = (nextSettings: any) => {
    if (nextSettings === 'new-version') {
      setIsCreationMode(true);
      setReadonly(false);
      setActiveSettings(null);
      resetToTemplateDefaults();

      return;
    }

    if (nextSettings?.id !== activeSettings?.id) {
      setActiveSettings(nextSettings);
    }
  };

  const onEditClick = () => {
    setIsCreationMode(!Boolean(activeSettings) || activeSettings.is_deployed);
    setReadonly(false);
  };

  const onDeployClick = () => {
    return deployConfigurationSettings({ project_id: id, settings_id: activeSettings.id });
  };

  const onCancelClick = () => {
    setReadonly(true);
    restoreActiveSettingsFallback();
  };

  const onSettingsCreated = (isManual: boolean) => {
    setSystemPromptActive(isManual);
    onSettingsChange('new-version');
  };

  const onFormClick = (e: any) => {
    if (shouldEnterEditModeOnClick(e, readonly)) {
      setReadonly(false);
    }
  };

  return (
    <PageContentWrapper
      projectBreadcrumbsContent={'Configuration'}
      className="project-page-container !gap-[15px] h-full !pb-0 !pt-[15px]"
      isMobileMarginTop={false}
    >
      {activeSettings && !activeSettings.is_deployed && (
        <NotDeployedSettingsPopup activeSettings={activeSettings} onDeployClick={onDeployClick} />
      )}
      {!readonly && <UnsavedChangesPopup readonly={readonly} />}
      <div className={clsx('flex items-center', settings ? 'justify-between' : 'justify-end')}>
        <ProjectTitle />
        <div className={'flex gap-2'}>
          {settings && (
            <ConfigurationSettingsVersions
              readonly={readonly}
              activeSettings={activeSettings}
              settings={settings}
              onChange={onSettingsChange}
              onCreate={onSettingsCreated}
            />
          )}
          <ConfigurationControls
            activeSettings={activeSettings}
            readonly={readonly}
            onEditClick={onEditClick}
            onCancelClick={onCancelClick}
            onDeployClick={onDeployClick}
          />
        </div>
      </div>
      <div
        className={clsx(
          'flex max-lg:flex-col gap-2.5 transition-all h-auto lg:h-[calc(100vh-160px)] lg:overflow-hidden',
        )}
      >
        <CardContainer
          className={'flex-1 min-w-px max-h-[100%] overflow-hidden !pt-2.5 relative z-10'}
        >
          <Tabs
            className="TabsRoot w-full h-full"
            defaultValue={activeTabId}
            onValueChange={setActiveTabId}
            value={activeTabId}
          >
            <TabsList
              className="w-full p-0 h-auto flex-wrap justify-start bg-transparent gap-5 md:gap-6"
              aria-label="Manage your account"
            >
              {systemPromptActive && (
                <TabsTrigger
                  className="!shadow-none font-bold data-[state=inactive]:text-ebony_opacity_30 data-[state=inactive]:hover:underline"
                  value="system-prompt"
                >
                  System Prompt
                </TabsTrigger>
              )}
              {!systemPromptActive && (
                <TabsTrigger
                  className="!shadow-none font-bold data-[state=inactive]:text-ebony_opacity_30 data-[state=inactive]:hover:underline"
                  value="persona"
                >
                  Persona
                </TabsTrigger>
              )}
              <TabsTrigger
                className="!shadow-none font-bold data-[state=inactive]:text-ebony_opacity_30 data-[state=inactive]:hover:underline"
                value="data-sources"
              >
                Data sources
              </TabsTrigger>
              {!systemPromptActive && (
                <TabsTrigger
                  className="!shadow-none font-bold data-[state=inactive]:text-ebony_opacity_30 data-[state=inactive]:hover:underline"
                  value="workflows"
                >
                  Workflows
                </TabsTrigger>
              )}
            </TabsList>
            <Separator className={'my-2.5 w-full bg-[#F1F6F6]'} />
            <div className={'w-full h-full pb-12'}>
              <FormProvider {...methods}>
                <SystemPromptTab
                  isCustomActiveSettings={Boolean(activeSettings?.is_custom)}
                  configurationSettingsPrompt={configurationSettingsPrompt}
                  customTemplatesData={customTemplatesData}
                  readonly={readonly}
                  isLoading={
                    isLoadingCreateConfigurationSettings || isLoadingUpdateConfigurationSettings
                  }
                  fullScreenBtnClick={fullScreenBtnClickHandler}
                  onEditClick={onEditClick}
                />
                <form
                  id={'configuration-page-form'}
                  onSubmit={methods.handleSubmit(submitForm)}
                  className={'h-full'}
                  onClick={onFormClick}
                >
                  <PersonaTab
                    isCustomActiveSettings={Boolean(activeSettings?.is_custom)}
                    isLoading={isLoading}
                    formFieldsObject={formFieldsObject}
                    inputData={inputData}
                    readonly={readonly}
                  />
                  <DataSourcesTab
                    readonly={readonly}
                    isActiveTab={activeTabId === 'data-sources'}
                  />
                  <WorkflowsTab
                    isLoading={isLoading}
                    formFieldsObject={formFieldsObject}
                    readonly={readonly}
                    defaultWorkflows={defaultWorkflows}
                    customWorkflows={customWorkflows}
                    onWorkflowsChange={onWorkflowsChange}
                    onWorkflowAdd={onWorkflowAdd}
                    onEditClick={onEditClick}
                  />
                </form>
              </FormProvider>
            </div>
          </Tabs>
        </CardContainer>
        <CardContainer
          className={clsx(
            'max-h-[100%] max-lg:min-h-[75vh] overflow-hidden !pt-2.5 relative shrink-0 transition-all duration-500 ease-out',
            isFullScreen ? 'w-0 !p-0 !border-0' : 'w-4/12 min-w-96 max-lg:w-full',
          )}
        >
          {!readonly && (
            <div
              className={
                'top-0 left-0 absolute w-full h-full bg-white opacity-40 pointer-events-none select-none z-10'
              }
            ></div>
          )}
          {activeSettings ? (
            <PlaygroundChat
              disabled={isInactivePlan}
              name={'configuration_chat'}
              isTitle
              settings={activeSettings}
              titleClassName={`!text-[#7A6BE5] !text-sm`}
              className={clsx('h-full', !readonly && 'pointer-events-none select-none')}
            />
          ) : (
            <div
              className={
                'bg-[#F5FCFE] w-full self-start rounded-sm-5 border border-[#009CB240] flex items-center justify-center p-7.5 mt-8'
              }
            >
              <p className={'text-center text-base font-medium text-[#009CB2]'}>
                Complete all required fields in the Configuration tab to enable the Playground.
              </p>
            </div>
          )}
        </CardContainer>
      </div>
    </PageContentWrapper>
  );
};

export default ConfigurationPage;
